import { openDB, DBSchema, IDBPDatabase } from "idb";

interface TimetableDBSchema extends DBSchema {
  timetables: {
    key: string;
    value: any;
  };
  // Add eTags store for caching eTags by URL
  eTags: {
    key: string; // The base blob URL (e.g., "https://account.blob.core.windows.net/container/path")
    value: {
      id: string; // Same as key for consistency
      eTag: string;
      updatedAt: number; // Timestamp for potential cleanup
    };
  };
}

class TimetableDatabase {
  private dbPromise: Promise<IDBPDatabase<TimetableDBSchema>> | null = null;

  // Azure best practice: Initialize DB with proper versioning
  private initDB(): Promise<IDBPDatabase<TimetableDBSchema>> {
    return openDB<TimetableDBSchema>("M247TimetablesDB", 3, {
      // Increment version to add new store
      upgrade(db, oldVersion, newVersion) {
        // Existing store
        if (!db.objectStoreNames.contains("timetables")) {
          db.createObjectStore("timetables");
        }
        // New eTags store
        if (!db.objectStoreNames.contains("eTags")) {
          db.createObjectStore("eTags");
        }
      },
    });
  }

  private async getDB(): Promise<IDBPDatabase<TimetableDBSchema>> {
    if (!this.dbPromise) {
      this.dbPromise = this.initDB();
    }
    try {
      return await this.dbPromise;
    } catch (error) {
      console.warn("IndexedDB open failed, attempting recovery:", error);
      this.dbPromise = null;
      try {
        await new Promise<void>((resolve, reject) => {
          const req = indexedDB.deleteDatabase("M247TimetablesDB");
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
          req.onblocked = () => resolve();
        });
        console.warn("Corrupted M247TimetablesDB deleted, recreating");
      } catch (deleteErr) {
        console.error("Failed to delete corrupted DB:", deleteErr);
      }
      this.dbPromise = this.initDB();
      return this.dbPromise;
    }
  }

  // Generate a consistent key format
  private getKey(year: string, month: string, masjidId: string): string {
    // Ensure month is always a number (no leading zero)
    const normalizedMonth = String(Number(month));
    return `timetable_${masjidId}_${year}_${normalizedMonth}`;
  }

  async getTimetable(
    year: string,
    month: string,
    masjidId: string,
  ): Promise<any> {
    try {
      const db = await this.getDB();
      const key = this.getKey(year, month, masjidId);
      return await db.get("timetables", key);
    } catch (error) {
      console.error("Error getting timetable from IndexedDB:", error);
      return null;
    }
  }

  async saveTimetable(
    year: string,
    month: string,
    masjidId: string,
    data: any,
  ): Promise<void> {
    try {
      const db = await this.getDB();
      const key = this.getKey(year, month, masjidId);
      await db.put("timetables", data, key);
    } catch (error) {
      console.error("Error saving timetable to IndexedDB:", error);
    }
  }

  async clearAll(): Promise<void> {
    try {
      const db = await this.getDB();
      await db.clear("timetables");
    } catch (error) {
      console.error("Error clearing timetables from IndexedDB:", error);
    }
  }

  // New methods for eTags
  async getETag(url: string): Promise<{ eTag?: string } | null> {
    try {
      const db = await this.getDB();
      const result = await db.get("eTags", url);
      if (result && typeof result.eTag === "string") {
        return { eTag: result.eTag };
      }
      return null;
    } catch (error) {
      console.error("Error getting eTag from IndexedDB:", error);
      return null;
    }
  }

  async saveETag(url: string, eTag: string): Promise<void> {
    try {
      const db = await this.getDB();
      await db.put(
        "eTags",
        {
          id: url,
          eTag,
          updatedAt: Date.now(),
        },
        url,
      );
      console.log(`eTag saved for URL: ${url}`);
    } catch (error) {
      console.error("Error saving eTag to IndexedDB:", error);
    }
  }

  async clearETags(): Promise<void> {
    try {
      const db = await this.getDB();
      await db.clear("eTags");
      console.log("All eTags cleared from IndexedDB");
    } catch (error) {
      console.error("Error clearing eTags from IndexedDB:", error);
    }
  }
}

export const timetableDB = new TimetableDatabase();
